import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
    Avatar, Box, Button, Container, Grid, Paper, 
    Typography, useTheme, Stack, Divider, Tooltip, useMediaQuery, Chip 
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faArrowLeft, faCalendarAlt, faCircleCheck, faMapMarkerAlt, 
    faPhone, faStar, faTags, faMapLocation, faHandshake, faShip, faUtensils, faArrowDown 
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { motion } from "framer-motion";

// --- Importaciones QUD ---
import ProductsCarousel from '../../../components/carousel';
import { imagesURL } from "../../../apiRoutes/apiBase";
import { createView } from "../../../controllers/customer/ViewsController";
import SocialComponent from "../../../components/socialComponent";
import MapContainer from '../../../components/map';
import QUDProgress from "../../../components/progress";
import Review from "../../../components/review";
import Reserve from "../../../components/reserve";
import ReadOnlyContent from "../../../components/ReadOnlyContent";
import PublicScheduleViewer from "../../../components/schedules/PublicScheduleViewer";
import { getMerchantData } from "../../../controllers/public/MerchantPublicController";



const mockAllies = [
    { id: 1, name: "Restaurante El Faro", category: "Gastronomía", icon: faUtensils, logo: "https://via.placeholder.com/150?text=El+Faro", desc: "Desayunos y cenas con vista al mar." },
    { id: 2, name: "Marina Tour", category: "Paseos", icon: faShip, logo: "https://via.placeholder.com/150?text=Marina", desc: "Traslados a las islas y paseos en lancha privada." },
    { id: 3, name: "Spa Azul", category: "Relajación", icon: faHandshake, logo: "https://via.placeholder.com/150?text=Spa", desc: "Masajes y terapias frente a la playa." },
    { id: 4, name: "Polar", category: "Bebidas", icon: faHandshake, logo: "https://via.placeholder.com/80?text=Polar", desc: "Proveedor oficial." }
];

/**
 * Componente MerchantProfile
 * Implementa un layout Immersive Hero replicando el diseño de la imagen de referencia.
 * @component
 * @returns {JSX.Element}
 */
export default function PublicMerchantProfile() {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { uuid } = useParams();
    const history = useHistory();

    const [merchant, setMerchant] = useState({});
    const [socials, setSocials] = useState([]);
    const [products, setProducts] = useState([]);
    const [events,setEvents] = useState([]);
    const [reviews,setReviews] = useState([]);
    const [workSchedules, setWorkSchedules] = useState([]);
    
    const [isLoading, setIsLoading] = useState(true);
    const [openReview, setOpenReview] = useState(false);
    const [openReserve, setOpenReserve] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        handleGetData();
        if (localStorage.getItem('openModalReview') === 'true') setOpenReview(true);
        if (localStorage.getItem('openModalReserve') === 'true') setOpenReserve(true);
    }, [uuid]);

    const handleGetData = async () => {
        setIsLoading(true);
        try {
            const response = await getMerchantData(uuid);
            if (response?.success) {
                setMerchant(response.data);
                setSocials(response.data.redes || []);
                setProducts(response.data.featured_products || []);
                setEvents(response.data.upcoming_events || []);
                setReviews(response.data.latest_reviews || []);
                setWorkSchedules(response.data.schedules || []);
                createView('merchant', response.data.uuid, response.data.uuid);
            }
        } catch (error) {
            console.error('Error fetching merchant data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const redirect = (route) => history.push(route);
    const closeReview = () => { localStorage.removeItem('openModalReview'); setOpenReview(false); };
    const closeReserve = () => { localStorage.removeItem('openModalReserve'); setOpenReserve(false); };

    // Tu Action Bar de 5 iconos/texto, centralizado en la tarjeta flotante
    const quickActions = [
        { id: 'contact', icon: faWhatsapp, label: t('actions.contact'), action: () => window.open(`https://wa.me/${merchant.phone}`, '_blank') },
        { id: 'directions', icon: faMapLocation, label: t('actions.directions'), action: () => window.open(`https://www.google.com/maps/search/?api=1&query=${merchant.latitude},${merchant.longitude}`, '_blank') },
        { id: 'reserve', icon: faCalendarAlt, label: t('actions.book'), action: () => setOpenReserve(true) },
        { id: 'review', icon: faStar, label: t('actions.review'), action: () => setOpenReview(true) },
        ...(products?.length > 0 ? [{ id: 'catalog', icon: faTags, label: t('actions.catalog'), action: () => redirect(`/merchants/${merchant.uuid}/menu`) }] : [])
    ];

    if (isLoading && !merchant.name) return <QUDProgress open={true} />;

    return (
        <Box sx={{ overflowX: 'hidden', pb: 10,mt:-6, bgcolor: '#f4f6f8' }}>
            <QUDProgress open={isLoading} />

            {/* ============================================================ */}
            {/* 1. IMMERSIVE HERO TOTAL - OPTIMIZADO PARA MÓVIL Y ESCRITORIO */}
            {/* ============================================================ */}
            <Box sx={{ 
                width: '100vw',
                /* En móvil dejamos que la altura sea automática pero mínimo el alto de pantalla, en PC fijo a 90vh */
                minHeight: { xs: 'calc(100vh - 80px)', md: '90vh' },
                height: { xs: 'auto', md: '90vh' },
                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.8) 100%), url(${merchant.cover_image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                /* Padding ajustado: en móvil un poco más de aire arriba por el nav */
                //p: { xs: 2, md: 8 },
                pt: { xs: 6, md: 8 },
                pb: { xs: 3, md: 8 },
            }}>
                <Container maxWidth="xl" sx={{ 
                    height: '100%', 
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    /* En móvil empujamos el texto arriba y la tarjeta abajo. En PC lo centramos */
                    justifyContent: { xs: 'space-between', md: 'center' },
                    minHeight: { xs: 'calc(100vh - 120px)', md: 'auto' }
                }}>
                    
                    {/* Texto Principal Grande y Minimalista */}
                    <Box sx={{ 
                        /* En móvil se comporta como un bloque normal flex, en PC es flotante absoluto */
                        position: { xs: 'relative', md: 'absolute' }, 
                        top: { md: 0 }, 
                        left: { md: 0 }, 
                        maxWidth: { md: '60%' },
                        zIndex: 10,
                        mt: { xs: 4, md: 0 }
                    }}>
                        <Chip label={merchant.category} size="small" sx={{ mb: 2, fontWeight: 'bold', bgcolor: 'rgba(255,255,255,0.1)', color: '#fff' }} />
                        <Typography variant="h1" fontWeight="900" sx={{ color: '#fff', fontSize: { xs: '2.5rem', sm: '3rem', md: '5rem' }, lineHeight: 1.1, letterSpacing: '-1px' }}>
                            {merchant.merchant}
                            {merchant.certified === 1 && (
                                <Tooltip title="Comercio Certificado">
                                    <Box component="span" sx={{ ml: 1.5, color: theme.palette.primary.main }}>
                                        <FontAwesomeIcon icon={faCircleCheck} size="xs" />
                                    </Box>
                                </Tooltip>
                            )}
                        </Typography>
                        <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.9)', mt: 1.5, fontStyle: 'italic', fontWeight: '400', fontSize: { xs: '1.1rem', md: '1.8rem' } }}>
                            "{merchant.slogan}"
                        </Typography>
                    </Box>

                    {/* Círculos de 'Scroll Down' Minimalistas */}
                    {!isMobile && (
                        <Stack direction="row" spacing={-2} alignItems="center" sx={{ position: 'absolute', bottom: 50, right: 0 }}>
                            {[1, 2].map((i) => (
                                <Box key={i} sx={{ width: 60, height: 60, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
                                    {i === 2 && <FontAwesomeIcon icon={faArrowDown} color="#fff" />}
                                </Box>
                            ))}
                        </Stack>
                    )}

                    {/* 2. LA TARJETA DE CONVERSIÓN FLOTANTE */}
                    <motion.div 
                        initial={{ opacity: 0, x: -30 }} 
                        animate={{ opacity: 1, x: 0 }} 
                        transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }} 
                        style={{ 
                            /* En móvil ocupa el flujo normal abajo, en PC flota absoluto */
                            position: isMobile ? 'relative' : 'absolute', 
                            bottom: isMobile ? 'auto' : '5%', 
                            left: 0, 
                            marginTop: isMobile ? 'auto' : 0, /* Auto empuja hacia abajo en flex */
                            zIndex: 20,
                            width: '100%',
                            paddingBottom: isMobile ? '20px' : 0
                        }}
                    >
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                borderRadius: 6, 
                                p: { xs: 2.5, md: 5 }, 
                                maxWidth: { xs: '100%', md: 500, lg:600 },
                                // --- EFECTO GLASSMORPHISM ---
                                bgcolor: 'rgba(255, 255, 255, 0.1)', 
                                backdropFilter: 'blur(20px)', 
                                WebkitBackdropFilter: 'blur(20px)', 
                                border: '1px solid rgba(255, 255, 255, 0.1)', 
                                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.3)', 
                            }}
                        >
                            <Grid container spacing={2} alignItems="center">
                                {/* Logo de QUD */}
                                <Grid item xs={12} sm="auto" sx={{ display: { xs: 'flex', sm: 'block' }, justifyContent: 'center' }}>
                                    <Avatar
                                        variant="rounded"
                                        src={merchant.profile_image}
                                        sx={{ 
                                            width: { xs: 80, sm: 100, md: 120 }, 
                                            height: { xs: 80, sm: 100, md: 120 }, 
                                            border: '3px solid rgba(255,255,255,0.2)', 
                                            boxShadow: '0px 8px 24px rgba(0,0,0,0.1)',
                                            bgcolor: 'rgba(255,255,255,0.2)' 
                                        }}
                                    />
                                </Grid>
                                {/* Info del comercio y Rating */}
                                <Grid item xs={12} sm sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                                    <Typography variant="h6" fontWeight="bold" sx={{ color: '#fff', lineHeight: 1.2 }}>{merchant.merchant}</Typography>
                                    <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.8)' }}>
                                        {merchant.city} • <FontAwesomeIcon icon={faStar} color="#f5b301" /> 5.0
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: { xs: 'none', sm: 'block' } }}>Aprende más sobre nosotros e interactúa.</Typography>
                                </Grid>
                            </Grid>
                            
                            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                            
                            {/* Action Bar - Scroll horizontal en móvil */}
                            <Stack 
                                direction="row" 
                                justifyContent={{ xs: 'flex-start', sm: 'space-between' }}
                                spacing={1}
                                divider={<Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />}
                                sx={{
                                    overflowX: 'auto',
                                    pb: { xs: 1, md: 0 },
                                    /* Esconder barra de scroll para un look más limpio */
                                    '&::-webkit-scrollbar': { display: 'none' },
                                    msOverflowStyle: 'none',
                                    scrollbarWidth: 'none'
                                }}
                            >
                                {quickActions.map((btn) => (
                                    <Box key={btn.id} onClick={btn.action} sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        alignItems: 'center', 
                                        cursor: 'pointer', 
                                        p: 1, 
                                        borderRadius: 2, 
                                        minWidth: { xs: '75px', sm: 'auto' }, /* Evita que se aplasten en móvil */
                                        transition: 'background 0.2s', 
                                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' } 
                                    }}>
                                        <FontAwesomeIcon size={isMobile ? "lg" : "xl"} color="#fff" icon={btn.icon} style={{ marginBottom: '8px' }} />
                                        <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem', color: '#fff', textAlign: 'center' }}>{btn.label}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Paper>
                    </motion.div>

                </Container>
            </Box>


            {/* 3. ASYMMETRICAL BODY (70/30) - Mantenido para el resto del contenido */}
            <Container maxWidth="xl" sx={{ mt: isMobile ? 12 : 10 }}>
                <Grid container spacing={5}>
                    
                    {/* COLUMNA IZQUIERDA: Descubrimiento (70%) */}
                    <Grid item xs={12} lg={8}>
                        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
                            
                            {/* Descripción */}
                            <Box sx={{ mb: 6 }}>
                                <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>El Concepto</Typography>
                                <Box sx={{ color: '#444', lineHeight: 1.8, fontSize: '1.1rem' }}>
                                    <ReadOnlyContent htmlContent={merchant.descripcion} />
                                </Box>
                            </Box>

                            {/* ALIADOS PRINCIPALES / SERVICIOS (Nuevo Protagonismo) *
                            {mockAllies?.length > 0 && (
                                <Box sx={{ mb: 6 }}>
                                    <Typography variant="h5" fontWeight="bold" mb={3}>Nuestros Espacios y Servicios</Typography>
                                    <Grid container spacing={2}>
                                        {mockAllies.slice(0, 4).map(ally => (
                                            <Grid item xs={12} sm={6} md={3} key={ally.id}>
                                                <Paper elevation={0} sx={{ border: '1px solid #eaeaea', borderRadius: 3, overflow: 'hidden', bgcolor: '#fff', height: '100%', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.05)' } }}>
                                                    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                                        <Avatar src={ally.logo} variant="rounded" sx={{ width: 60, height: 60, border: '1px solid #eee', mb: 1.5 }} />
                                                        <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>{ally.name}</Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                                            <FontAwesomeIcon icon={ally.icon} size="xs" /> {ally.category}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, fontSize: '0.8rem' }}>{ally.desc}</Typography>
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}*/}

                            {/* PRODUCTOS DESTACADOS */}
                            {products?.length > 0 && (
                                <Box sx={{ mb: 6 }}>
                                    <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>Productos Destacados</Typography>
                                    <Box sx={{ bgcolor: '#fff', p: { xs: 2, md: 3 }, borderRadius: 3, border: '1px solid #eaeaea' }}>
                                        <ProductsCarousel items={products} merchant={merchant} merchant_uuid={merchant.uuid} />
                                    </Box>
                                </Box>
                            )}

                            {/* Próximos Eventos */}
                            {events?.length > 0 && (
                                <Box sx={{ mb: 6 }}>
                                    <Typography variant="h5" fontWeight="bold" mb={3}>Próximos Eventos</Typography>
                                    <Grid container spacing={3}>
                                        {events.map(event => (
                                            <Grid item xs={12} sm={6} key={event.id}>
                                                <Paper elevation={0} sx={{ overflow: 'hidden', border: '1px solid #eaeaea', borderRadius: 3, bgcolor: '#fff', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
                                                    <Box sx={{ height: 160, backgroundImage: `url(${event.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                                                    <Box sx={{ p: 2.5 }}>
                                                        <Chip label={event.type} size="small" sx={{ mb: 1.5, fontWeight: 'bold', bgcolor: 'rgba(0, 118, 182, 0.1)', color: '#0076B6' }} />
                                                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, lineHeight: 1.2 }}>{event.name}</Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}><FontAwesomeIcon icon={faCalendarAlt} /> {event.date} • {event.time}</Typography>
                                                        <Button 
                                                            variant="outlined" fullWidth size="small" 
                                                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', borderColor: '#0076B6', color: '#0076B6' }}
                                                            onClick={() => redirect(`/eventos/${event.id}`)} 
                                                        >
                                                            Obtener Entradas
                                                        </Button>
                                                    </Box>
                                                </Paper>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                            )}

                            {/* Reseñas */}
                            <Box sx={{ mb: 4 }}>
                                <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>Experiencias Recientes</Typography>
                                
                                <Grid container spacing={3} alignItems="stretch">
                                    {reviews.map((review, index) => (
                                        
                                        <Grid 
                                            item 
                                            xs={12} sm={6} md={6} 
                                            key={review.id || index} 
                                            // 1. Aseguramos que el contenedor del Grid maneje bien la columna
                                            sx={{ display: 'flex', flexDirection: 'column' }} 
                                        >
                                            <Paper 
                                                elevation={0} 
                                                sx={{ 
                                                    p: 3, 
                                                    border: '1px solid #eaeaea', 
                                                    borderRadius: 3, 
                                                    
                                                    // 2. ELIMINAMOS height: '100%' y width: '100%'
                                                    // 3. AGREGAMOS flex: 1 para que ocupe el espacio sin romperse
                                                    flex: 1, 
                                                    
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography fontWeight="bold">{review.user_name}</Typography>
                                                    <Typography color="#f5b301"><FontAwesomeIcon icon={faStar} /> {review.score}</Typography>
                                                </Box>
                                                
                                                <Typography 
                                                    variant="body2" 
                                                    color="text.secondary" 
                                                    sx={{ mb: 2, flexGrow: 1 }} 
                                                >
                                                    "{review.comment}"
                                                </Typography>
                                                
                                                <Typography variant="caption" color="#999">
                                                    {review.date}
                                                </Typography>
                                            </Paper>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        </motion.div>
                    </Grid>

                    {/* COLUMNA DERECHA: Operativa (30% - Sticky) */}
                    <Grid item xs={12} lg={4}>
                        <Box sx={{ position: { lg: 'sticky' }, top: { lg: 100 } }}>
                            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                                
                                {/* Ubicación y Contacto */}
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 4, mb: 4, border: '1px solid #eaeaea', bgcolor: '#fff' }}>
                                    <Typography variant="h6" fontWeight="bold" mb={3}>Contacto y Ubicación</Typography>

                                    {/* Info de Contacto */}
                                    <Stack spacing={2.5} sx={{ mb: 3 }}>
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                            <Box sx={{ mt: 0.5, minWidth: 24, display: 'flex', justifyContent: 'center' }}>
                                                <FontAwesomeIcon icon={faMapMarkerAlt} color="#0076B6" size="lg" />
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                                {merchant.address}, {merchant.city}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                            <Box sx={{ minWidth: 24, display: 'flex', justifyContent: 'center' }}>
                                                <FontAwesomeIcon icon={faPhone} color="#0076B6" size="lg" />
                                            </Box>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: '500' }}>
                                                +{merchant.phone}
                                            </Typography>
                                        </Box>
                                    </Stack>

                                    {/* Redes Sociales - Wrapper de seguridad visual */}
                                    {socials?.length > 0 && (
                                        <Box sx={{ mb: 4 }}>
                                            <Divider sx={{ mb: 2 }} />
                                            <Stack direction="row" spacing={1.5} flexWrap="wrap">
                                                {socials.map((item, idx) => (
                                                    <Box 
                                                        key={idx} 
                                                        sx={{ 
                                                            p: 0.5, 
                                                            pr: 1.5,
                                                            border: '1px solid #eee', 
                                                            borderRadius: 2,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            transition: 'all 0.2s',
                                                            bgcolor: '#fafafa', // Fondo gris muy claro para forzar contraste
                                                            '&:hover': { borderColor: '#0076B6', bgcolor: 'rgba(0,118,182,0.05)' }
                                                        }}
                                                    >
                                                        {/* NOTA: Revisa tu componente interno. Si sigue sin verse bien, 
                                                            es porque tiene hardcodeado el color blanco adentro. */}
                                                        <SocialComponent 
                                                            social={item.nombre} 
                                                            username={item.username} 
                                                            link={item.enlace} 
                                                            color="#0076B6" 
                                                        />
                                                    </Box>
                                                ))}
                                            </Stack>
                                        </Box>
                                    )}

                                    {/* Mapa Enmarcado (Inset Map) */}
                                    <Box sx={{ 
                                        height: 200, 
                                        borderRadius: 3, 
                                        overflow: 'hidden', 
                                        border: '1px solid #eaeaea', // Borde sutil para enmarcarlo
                                        position: 'relative',
                                        boxShadow: 'inset 0px 0px 10px rgba(0,0,0,0.05)' // Sombra interna ligera
                                    }}>
                                        <MapContainer 
                                            center={{ lat: parseFloat(merchant.latitude), lng: parseFloat(merchant.longitude) }} 
                                            coordenadas={{ lat: parseFloat(merchant.latitude), lng: parseFloat(merchant.longitude) }} 
                                        />
                                    </Box>
                                </Paper>

                                {/* Horarios */}
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 4, mb: 4, border: '1px solid #eaeaea', bgcolor: '#fff' }}>
                                    <Typography variant="h6" fontWeight="bold" mb={2}>Horarios</Typography>
                                    <PublicScheduleViewer schedules={workSchedules} />
                                </Paper>

                                {/* Aliados Comerciales (Directorio Completo) */}
                                <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid #eaeaea', bgcolor: '#fff' }}>
                                    <Typography variant="h6" fontWeight="bold" mb={3}><FontAwesomeIcon icon={faHandshake} color="#0076B6" /> Directorio de Aliados</Typography>
                                    <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', pb: 1, '::-webkit-scrollbar': { height: '6px' }, '::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '10px' } }}>
                                        <Typography>Próximamente</Typography>
                                        {/*mockAllies.map(ally => (
                                            <Box key={ally.id} sx={{ textAlign: 'center', minWidth: 70 }}>
                                                <Avatar src={ally.logo} variant="rounded" sx={{ width: 60, height: 60, mx: 'auto', mb: 1, border: '1px solid #eee', bgcolor: '#fafafa' }} />
                                                <Typography variant="caption" fontWeight="bold" color="text.secondary">{ally.name}</Typography>
                                            </Box>
                                        ))*/}
                                    </Stack>
                                </Paper>

                            </motion.div>
                        </Box>
                    </Grid>

                </Grid>
            </Container>

            {/* Modales */}
            <Review type='merchant' toUUID={merchant.uuid} name={merchant.merchant} image={`${imagesURL}empresas/${merchant.profile_image}`} open={openReview} handleClose={closeReview} />
            <Reserve type='merchant' toUUID={merchant.uuid} name={merchant.merchant} image={`${imagesURL}empresas/${merchant.profile_image}`} open={openReserve} handleClose={closeReserve} />
        </Box>
    );
}